import { createContext, ReactNode, useCallback, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';

import {
  GLOBAL_ACTIONS,
  GLOBAL_RESOURCES,
  GlobalAction,
  GlobalResource,
} from 'src/constants';
import { initialGlobalEvaluations } from 'src/context/initialGlobalEvaluations';
import { useEvaluations } from 'src/hooks/api';

/**
 * Context value for global/module-level evaluations.
 *
 * Covers actions that are NOT scoped to a resource instance (no UUID). These
 * are pre-fetched once on app load and shared throughout the app.
 *
 * For instance-scoped permissions (e.g. actions on a specific ApplicationList
 * or Tenant UUID), use `useInstanceEvaluations` instead.
 */
export interface GlobalEvaluationsContextValue {
  /**
   * Helper function to evaluate an action for a resource.
   */
  can: (params: { action: GlobalAction; resource: GlobalResource }) => boolean;
  /**
   * Helper to check if the user is authorized for the application.
   */
  canUseApp: boolean;
  /**
   * The raw error from the evaluations query, if any.
   * At runtime this is typically a `Response` object thrown by `apiRequest`.
   */
  error: unknown;
  /**
   * Error state.
   */
  isError: boolean;
  /**
   * Loading state.
   */
  isLoading: boolean;
  /**
   * React Query function to manually refetch.
   */
  refetch: () => void;
}

/**
 * Provides global/module-level evaluation results.
 *
 * Only covers actions without a resource instance ID. Use
 * `useInstanceEvaluations` for instance-scoped permissions.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const GlobalEvaluationsContext = createContext<
  GlobalEvaluationsContextValue | undefined
>(undefined);

export const GlobalEvaluationsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { isAuthenticated, user } = useAuth();

  // Only fetch evaluations if user is authenticated
  const enabled = isAuthenticated;

  const { data, error, isError, isPending, refetch } = useEvaluations({
    evaluations: initialGlobalEvaluations,
    options: {
      enabled,
    },
    subject: {
      id: user?.profile.sub,
      type: 'user',
    },
  });

  // Check if the user has the required evaluations
  const can = useCallback(
    ({
      action,
      resource,
    }: {
      action: GlobalAction;
      resource: GlobalResource;
    }) => {
      if (!data) return false;

      return data.evaluations.some(
        ({ context, decision }) =>
          decision &&
          context?.resource.type === resource &&
          context?.action.name === action,
      );
    },
    [data],
  );

  // Check if the user is allowed to use the app at all
  const canUseApp = useMemo(
    () =>
      !isError &&
      can({
        action: GLOBAL_ACTIONS.APP_ACCESS,
        resource: GLOBAL_RESOURCES.APP,
      }),
    [can, isError],
  );

  /**
   * Whether the evaluations query failed due to an authentication/authorization
   * error (401/403). Used to show a loading state while redirecting to sign in,
   * rather than flashing a 404 page.
   */
  const isAuthError =
    isError &&
    error instanceof Response &&
    (error.status === 401 || error.status === 403);

  return (
    <GlobalEvaluationsContext.Provider
      value={{
        can,
        canUseApp,
        error,
        isError,
        /**
         * When `enabled` changes from false to true, React Query doesn't
         * immediately set `isPending` to true. Check `!data` to prevent race
         * condition where routes render before evaluations are fetched.
         * Also check `!isError` to avoid infinite loading on API failures.
         *
         * For auth errors (401/403), force loading state to prevent the 404
         * page from flashing while the sign in redirect is being triggered.
         */
        isLoading: enabled
          ? isAuthError || ((isPending || !data) && !isError)
          : false,
        refetch,
      }}
    >
      {children}
    </GlobalEvaluationsContext.Provider>
  );
};
